// src/pages/MyAffiliate.js (updated with Notifications)
import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    CardContent,
    Drawer,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Box,
    Tabs,
    Tab,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import { getAffiliate, updateAffiliate } from '../api/affiliateApi';
import AffiliateView from '../components/AffiliateView';
import FinanceView from '../components/FinanceView';
import AffiliateContracts from '../components/AffiliateContracts';
import UnpaidUsers from '../components/UnPaidUsers';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import { Paper } from '@mui/material';
import AffiliateTerms from '../components/affiliateTerms';
import MessageTemplates from '../components/MessageTemplates';
import Notifications from '../components/Notifications'; // Import the new component
import TrainerSchedule from '../components/TrainerSchedule';
import AffiliateReports from "../components/AffiliateReports";
import Agreement from "../pages/Agreement";
import AffiliateCompetitions from '../components/AffiliateCompetitions';
import AffiliateEvents from '../components/AffiliateEvents';
import GiftCardManagement from '../components/GiftCardManagement';
import DiscountCodeManagement from '../components/DiscountCodeManagement';
import ShopManagement from '../components/ShopManagement';
import ChangePassword from '../components/ChangePassword';
import SmartLockSettings from '../components/SmartLockSettings';
import WorkflowManagement from '../components/WorkflowManagement';
import { changeUserPassword } from '../api/profileApi';
import { useLocation, useNavigate } from 'react-router-dom';

const pricingPlan = localStorage.getItem('pricingPlan');

const menuItems = [
    { id: 'my-affiliate', label: 'My Affiliate', component: AffiliateView },
    { id: 'analytics', label: 'Analytics', component: AnalyticsDashboard },
    { id: 'finance', label: 'Finance', component: FinanceView },
    { id: 'notifications', label: 'Notifications', component: Notifications }, // Add notifications menu item
    { id: 'trainer-schedule', label: 'Trainer Schedule', component: TrainerSchedule },
    { id: 'reports', label: 'Reports', component: AffiliateReports },
    { id: 'agreement', label: 'Agreement', component: Agreement },
    { id: 'terms', label: 'Terms & Conditions', component: AffiliateTerms },
    { id: 'gift-cards', label: 'Gift Cards', component: GiftCardManagement },
    ...(pricingPlan === 'premium' ? [
        { id: 'workflow', label: 'Workflow', component: WorkflowManagement },
        { id: 'events', label: 'Events', component: AffiliateEvents },
        { id: 'smart-lock', label: 'Smart Lock', component: SmartLockSettings },
        { id: 'shop', label: 'Shop', component: ShopManagement },
        { id: 'discount-codes', label: 'Discount Codes', component: DiscountCodeManagement },
        { id: 'competitions', label: 'Competitions', component: AffiliateCompetitions },
        { id: 'contracts', label: 'Contracts', component: AffiliateContracts },
        { id: 'unpaid-users', label: 'Unpaid Users', component: UnpaidUsers },
        { id: 'message-templates', label: 'Automated Messages', component: MessageTemplates },
    ] : [])
];

export default function MyAffiliate() {
    const location = useLocation();
    const navigate = useNavigate();
    const [affiliate, setAffiliate] = useState(null);
    const [trainers, setTrainers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeComponent, setActiveComponent] = useState(
        location.state?.activeComponent || 'my-affiliate'
    );
    const [createContractForUser, setCreateContractForUser] = useState(
        location.state?.createContractForUser || null
    );
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Teavituste state'id
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' // 'success', 'error', 'warning', 'info'
    });

    const [dialog, setDialog] = useState({
        open: false,
        title: '',
        message: '',
        type: 'success' // 'success' või 'error'
    });

    useEffect(() => {
        getAffiliate()
            .then((data) => {
                setAffiliate(data.affiliate || {});
                setTrainers(data.trainers || []);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching affiliate:', error);
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        if (location.state?.createContractForUser) {
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.pathname, location.state, navigate]);

    const handleMenuClick = (componentId) => {
        setActiveComponent(componentId);
        setDrawerOpen(false);
    };

    // Teavituste funktsioonid
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const showDialog = (title, message, type = 'success') => {
        setDialog({
            open: true,
            title,
            message,
            type
        });
    };

    const closeSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const closeDialog = () => {
        setDialog(prev => ({ ...prev, open: false }));
    };

    // Handler for Change Password
    const handleChangePassword = () => {
        setActiveComponent('change-password-temp');
    };

    const handleChangePasswordSubmit = async (passwordData) => {
        const success = await changeUserPassword(passwordData);
        if (success) {
            showSnackbar('Password changed successfully!', 'success');
            setActiveComponent('my-affiliate');
        } else {
            showDialog('Password Change Failed', 'Failed to change password.', 'error');
        }
    };

    const handleUpdateAffiliate = async (updatedAffiliate) => {
        const success = await updateAffiliate(updatedAffiliate);
        if (success) {
            // ✅ Laadi andmed uuesti serverist
            try {
                const data = await getAffiliate();
                setAffiliate(data.affiliate || {});
                setTrainers(data.trainers || []);
            } catch (error) {
                console.error('Error refreshing affiliate data:', error);
            }
            setActiveComponent('my-affiliate');
            showSnackbar('Affiliate updated successfully!', 'success');
        } else {
            showDialog('Update Failed', 'Failed to update affiliate', 'error');
        }
    };

    // Valib aktiivse komponendi menüüst
    const ActiveComponent =
        menuItems.find((item) => item.id === activeComponent)?.component || AffiliateView;

    // Renderimise loogika
    const renderActiveComponent = () => {
        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                    <CircularProgress />
                </Box>
            );
        }

        // Special case for change-password component
        if (activeComponent === 'change-password-temp') {
            return (
                <ChangePassword
                    onChangePassword={handleChangePasswordSubmit}
                    onCancel={() => setActiveComponent('my-affiliate')}
                />
            );
        }

        // For all other components
        return (
            <ActiveComponent
                affiliate={affiliate}
                trainers={trainers}
                affiliateId={affiliate?.id}
                onUpdateAffiliate={handleUpdateAffiliate}
                affiliateEmail={affiliate?.email}
                getAffiliate={getAffiliate}
                onChangePassword={handleChangePassword}
                createContractForUser={createContractForUser}
                onCreateContractUserConsumed={() => setCreateContractForUser(null)}
            />
        );
    };

    return (
        <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', pl: {xs: '0 !important'}, pr: {xs: '0 !important'} }}>
            {/* Horisontaalne mobiilimenüü */}
            <Paper
                sx={{
                    position: 'static',
                    top: 45,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    display: { xs: 'block', md: 'none' },
                    overflow: 'hidden',
                }}
                elevation={1}
            >
                <Box
                    sx={{
                        overflowX: 'auto',
                        display: 'flex',
                        width: '100%',
                        WebkitOverflowScrolling: 'touch',
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: '20px',
                            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.9))',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Tabs
                        value={activeComponent === 'change-password-temp' ? 'my-affiliate' : activeComponent}
                        onChange={(event, newValue) => setActiveComponent(newValue)}
                        variant="scrollable"
                        scrollButtons={false}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                            minHeight: '36px',
                            width: '100%',
                            '& .MuiTabs-flexContainer': {
                                width: '100%',
                                display: 'flex',
                                '& > *': {
                                    flexGrow: 1,
                                },
                            },
                            '& .MuiTab-root': {
                                minHeight: '36px',
                                py: 0.75,
                                textTransform: 'none',
                            },
                        }}
                    >
                        {menuItems.map((item) => (
                            <Tab
                                key={item.id}
                                label={item.label}
                                value={item.id}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    minWidth: 'auto',
                                    fontWeight: (activeComponent === item.id || 
                                        (activeComponent === 'change-password-temp' && item.id === 'my-affiliate')) 
                                        ? 'bold' : 'normal',
                                    fontSize: '0.85rem',
                                    px: 1,
                                    backgroundColor: (activeComponent === item.id || 
                                        (activeComponent === 'change-password-temp' && item.id === 'my-affiliate'))
                                        ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>
            </Paper>

            <Box sx={{ display: "flex", flexGrow: 1 }}>
                {/* Sidebar Navigation desktop vaates */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        width: 240,
                        flexShrink: 0,
                        position: 'relative !important',
                    }}
                    PaperProps={{
                        sx: {
                            position: 'relative',
                        },
                    }}
                >
                    <Box sx={{ width: 220, p: 2 }}>
                        <List>
                            {menuItems.map((item) => (
                                <ListItem
                                    button
                                    key={item.id}
                                    onClick={() => handleMenuClick(item.id)}
                                    selected={activeComponent === item.id || 
                                        (activeComponent === 'change-password-temp' && item.id === 'my-affiliate')}
                                    sx={{
                                        borderRadius: '4px',
                                        mb: 0.5,
                                        pl: 2,
                                        cursor: 'pointer',
                                        position: 'relative',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(33, 150, 243, 0.15)',
                                            borderLeft: '5px solid #2196f3',
                                            pl: 1.5,
                                            '&:hover': {
                                                backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                            },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                right: 0,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                width: 0,
                                                height: 0,
                                                borderTop: '8px solid transparent',
                                                borderBottom: '8px solid transparent',
                                                borderRight: '8px solid #fff',
                                            }
                                        }
                                    }}
                                >
                                    <ListItemText 
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: (activeComponent === item.id || 
                                                (activeComponent === 'change-password-temp' && item.id === 'my-affiliate'))
                                                ? '600' : 'normal',
                                            fontSize: '0.95rem',
                                            color: (activeComponent === item.id || 
                                                (activeComponent === 'change-password-temp' && item.id === 'my-affiliate'))
                                                ? '#1976d2' : 'rgba(0, 0, 0, 0.7)'
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Drawer>

                {/* Main Content */}
                <Box sx={{ flexGrow: 1, pt: 2, pl: { xs: 0, md: 2, lg: 2 }, pr: { xs: 0, md: 2, lg: 2 } }}>
                    <Card sx={{pl: {xs: 0, md: 2, lg: 2}, pr: {xs: 0, md: 2, lg: 2}, width:  {xs: '100vw', md: '70vw', lg: '75vw'}}}>
                        <CardContent>
                            {renderActiveComponent()}
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Snackbar teavituste jaoks */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Dialog teavituste jaoks */}
            <Dialog
                open={dialog.open}
                onClose={closeDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {dialog.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {dialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}